import { COPY } from "../../copy";
import type { P8MonthlySalesStack } from "../../p8OverviewData";
import { renderCopy } from "../../renderCopy";

type TooltipPayloadItem = {
  dataKey?: string | number;
  payload?: P8MonthlySalesStack;
};

type MonthlyAdSalesTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipPayloadItem[];
  variant?: "default" | "updated";
};

const DEFAULT_SERIES = [
  {
    key: "new",
    color: "#93BBC9",
    description: (amount: string) =>
      renderCopy(COPY.overview.charts.bySource.hoverNewAds, { amount }),
  },
  {
    key: "reorder",
    color: "#E3773E",
    description: (amount: string) =>
      renderCopy(COPY.overview.charts.bySource.hoverReorderAds, { amount }),
  },
  {
    key: "previous",
    color: "#5D2E37",
    description: (amount: string) =>
      renderCopy(COPY.overview.charts.bySource.hoverPreviousMonths, { amount }),
  },
] as const;

/**
 * Copy for the "updated" variant, which only the archived by-source chart
 * renders. It stays here rather than in copy.ts because nothing reachable in the
 * prototype shows it.
 */
const UPDATED_SERIES = [
  {
    key: "new",
    color: "#93BBC9",
    description: (amount: string) =>
      `${amount} in immediate new customer ad sales this month`,
  },
  {
    key: "reorder",
    color: "#E3773E",
    description: (amount: string) =>
      `${amount} in immediate reorder ad sales this month`,
  },
  {
    key: "previous",
    color: "#C4D1FF",
    description: (amount: string) =>
      `${amount} in continued ad sales from customers acquired in previous months`,
  },
] as const;

function formatTooltipCurrency(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function MonthlyAdSalesTooltip({
  active,
  label,
  payload,
  variant = "default",
}: MonthlyAdSalesTooltipProps) {
  if (!active || !label || !payload?.length) {
    return null;
  }

  const series = variant === "updated" ? UPDATED_SERIES : DEFAULT_SERIES;

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  const activeKey = String(payload[0]?.dataKey ?? "");
  const total = point.new + point.reorder + point.previous;
  const values: Record<(typeof DEFAULT_SERIES)[number]["key"], number> = {
    new: point.new,
    reorder: point.reorder,
    previous: point.previous,
  };

  return (
    <div className="bg-fs-surface-primary rounded-lg p-4 shadow-[0_2px_10px_rgba(0,0,0,0.15)]">
      <p className="text-sm leading-5 font-medium text-[#222]">
        {formatTooltipCurrency(total)} ({label})
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {series.map((entry) => {
          const isActive = activeKey === entry.key;
          const amount = formatTooltipCurrency(values[entry.key]);

          return (
            <div key={entry.key} className="flex items-start gap-3">
              <span
                className="mt-0.5 size-3 shrink-0 rounded-sm"
                style={{ backgroundColor: entry.color }}
                aria-hidden
              />
              <p
                className={
                  isActive
                    ? "text-sm leading-5 font-medium text-[#222]"
                    : "text-sm leading-5 font-normal text-[#717171]"
                }
              >
                {entry.description(amount)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
